// =============================================================================
// src/lib/loginTint.js - tint the sign-in screen with the practice's palette.
//
// The sign-in screen runs before we know who the user is, so it cannot read
// practices.brand_palette. Instead the last palette applied after a sign-in
// on this device is remembered (localStorage key below, the palette KEY only,
// e.g. "navy" - no PHI) and the sign-in screen derives its accent colours
// from that palette's primary hue. With nothing remembered (first visit,
// cleared storage, private window) the original mint look is used unchanged.
//
//   rememberLoginPalette(name)   called after the palette is applied
//   loginTintVars(name?)         -> { "--pl-a": "r, g, b", ... } for .pl-root
//   rememberedLoginPalette()     -> key or null
//
// Variables (all "r, g, b" triplets, used as rgba(var(--pl-x), alpha)):
//   --pl-a   bright accent (labels, logo, focus border)
//   --pl-m   mid accent (glows, pulses)
//   --pl-d   deep accent (CTA bottom, checks)
//   --pl-d2  deeper accent (CTA bottom stop)
//   --pl-c   CTA top stop
//   --pl-s   soft accent (link hover, focus outline, headline start)
//   --pl-s2  softer accent / body text on tiles
//   --pl-ink text on the accent button
//   --pl-k   near-black tint (field background)
//   --pl-k2  slightly lighter near-black tint (focused field)
//   --pl-t   primary text
//   --pl-g1..g4 tinted greys, light to dark
// =============================================================================
import { BRAND_PALETTES } from "./brandPalette";

const KEY = "practiceos.login_palette";

export function rememberLoginPalette(name) {
  if (!name || !BRAND_PALETTES[name]) return;
  try { window.localStorage.setItem(KEY, name); } catch { /* storage blocked */ }
}

// "Welcome back to <practice>" on the sign-in screen. Practice name only
// (not patient data), remembered from a staff sign-in on this device.
const PRACTICE_KEY = "practiceos.login_practice";

export function rememberLoginPractice(name) {
  const v = String(name || "").trim().slice(0, 80);
  if (!v) return;
  try { window.localStorage.setItem(PRACTICE_KEY, v); } catch { /* storage blocked */ }
}

export function rememberedLoginPractice() {
  try { return window.localStorage.getItem(PRACTICE_KEY) || null; } catch { return null; }
}

export function rememberedLoginPalette() {
  try {
    const v = window.localStorage.getItem(KEY);
    return v && BRAND_PALETTES[v] ? v : null;
  } catch { return null; }
}

// The original mint values, used when no palette is remembered.
const DEFAULT_VARS = {
  "--pl-a": "127, 224, 188", "--pl-m": "93, 202, 165", "--pl-d": "47, 185, 136", "--pl-d2": "31, 158, 115",
  "--pl-c": "111, 224, 182", "--pl-s": "184, 242, 220", "--pl-s2": "200, 247, 228", "--pl-ink": "3, 20, 13",
  "--pl-k": "3, 14, 10", "--pl-k2": "10, 40, 30", "--pl-t": "239, 248, 244",
  "--pl-g1": "157, 185, 174", "--pl-g2": "134, 163, 152", "--pl-g3": "111, 143, 132", "--pl-g4": "63, 95, 84",
};

function hexToHsl(hex) {
  const h = String(hex || "").replace("#", "");
  const r = parseInt(h.slice(0, 2), 16) / 255, g = parseInt(h.slice(2, 4), 16) / 255, b = parseInt(h.slice(4, 6), 16) / 255;
  if (![r, g, b].every(Number.isFinite)) return null;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  const l = (max + min) / 2;
  let hue = 0, s = 0;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === r) hue = (g - b) / d + (g < b ? 6 : 0);
    else if (max === g) hue = (b - r) / d + 2;
    else hue = (r - g) / d + 4;
    hue *= 60;
  }
  return { h: hue, s, l };
}

function hslToRgbStr(h, s, l) {
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - c / 2;
  let rgb;
  if (h < 60) rgb = [c, x, 0]; else if (h < 120) rgb = [x, c, 0]; else if (h < 180) rgb = [0, c, x];
  else if (h < 240) rgb = [0, x, c]; else if (h < 300) rgb = [x, 0, c]; else rgb = [c, 0, x];
  return rgb.map((v) => Math.round((v + m) * 255)).join(", ");
}

// Same lightness / saturation steps as the mint design, on the palette's hue.
// Saturation follows the palette: a grey palette (black, slate) gives a
// neutral silver screen instead of an invented colour.
export function loginTintVars(name) {
  const key = name === undefined ? rememberedLoginPalette() : name;
  if (!key || !BRAND_PALETTES[key]) return DEFAULT_VARS;
  const hsl = hexToHsl(BRAND_PALETTES[key].primary);
  if (!hsl) return DEFAULT_VARS;
  const k = Math.min(1, hsl.s / 0.6);       // 0 for black, ~1 for saturated
  const H = hsl.h;
  const S = (base) => Math.max(0, Math.min(1, base * k));
  return {
    "--pl-a":   hslToRgbStr(H, S(0.62), 0.69),
    "--pl-m":   hslToRgbStr(H, S(0.52), 0.58),
    "--pl-d":   hslToRgbStr(H, S(0.60), 0.45),
    "--pl-d2":  hslToRgbStr(H, S(0.67), 0.37),
    "--pl-c":   hslToRgbStr(H, S(0.65), 0.66),
    "--pl-s":   hslToRgbStr(H, S(0.68), 0.83),
    "--pl-s2":  hslToRgbStr(H, S(0.72), 0.88),
    "--pl-ink": hslToRgbStr(H, S(0.74), 0.04),
    "--pl-k":   hslToRgbStr(H, S(0.55), 0.03),
    "--pl-k2":  hslToRgbStr(H, S(0.60), 0.10),
    "--pl-t":   hslToRgbStr(H, S(0.41), 0.95),
    "--pl-g1":  hslToRgbStr(H, S(0.15), 0.67),
    "--pl-g2":  hslToRgbStr(H, S(0.13), 0.58),
    "--pl-g3":  hslToRgbStr(H, S(0.13), 0.50),
    "--pl-g4":  hslToRgbStr(H, S(0.20), 0.31),
  };
}
